import { Box, Typography } from '@mui/material';
import { FileText, RefreshCw } from 'lucide-react';
import { useMemo, type ReactElement } from 'react';

import {
  createAdminApi,
  type AdminApi,
  type AdminLegalDocument,
  type AdminLegalDocumentType,
  type AdminRole,
} from '@/admin/adminApi';
import { formatCompactInstant } from '@/admin/data/payload';
import { useAdminQuery } from '@/admin/data/useAdminQuery';
import { useOperationsRefresh } from '@/admin/operations/useOperationsRefresh';
import { AccessPanel } from '@/admin/users/accountsUi';
import { useAdminAccess } from '@/admin/users/useAdminAccess';
import { useHttpClient } from '@/app/providers';
import { AccessDenied, Badge, Button, Card, ErrorState, Icon, LoadingState, PageHeader, useDetailRoute } from '@/ui/kit';

import { DocumentPanel } from './DocumentPanel';
import { LEGAL_DOCUMENT_TITLES, LEGAL_DOCUMENT_TYPES, isLegalDocumentType } from './legalDocument';
import { CONTENT_PATH } from './routes';

/**
 * Contenus (P18) : les textes que le mobile affiche aux parents, sans nouvelle
 * publication de l'application.
 *
 * Deux documents seulement, CGU et CGV : un type de plus casserait l'ecran des
 * versions installees (le mobile lit une enumeration stricte), et les lignes
 * « Confidentialite » et « Mentions legales » de la maquette seraient factices.
 * Ni onglet « Annonces » ni « Modeles de courriels » : aucune source cote serveur.
 *
 * Trois principes, ecrits sur l'ecran :
 *
 * 1. **Une version publiee ne se modifie plus.** On ecrit un brouillon, puis on
 *    publie : la publication est une action lourde (motif, saisie du type,
 *    mot de passe) qui cree la version suivante et remplace ce que lisent les
 *    parents. Aucun bouton « Supprimer » : rien n'est detruit.
 * 2. **Tout nombre vient du serveur** : numero de version, longueur, total de
 *    l'historique, numero de la prochaine publication.
 * 3. **Aucune relecture automatique** : « Actualiser » et le retour sur l'onglet
 *    relisent la liste et l'historique ; la saisie en cours n'est jamais ecrasee.
 *
 * Le document ouvert vit dans l'URL (`/content/cgv`), comme les autres ecrans a
 * selection : un rechargement ou un lien partage rouvrent le meme texte.
 */

const DocumentRow = ({
  type,
  doc,
  selected,
  onOpen,
}: {
  readonly type: AdminLegalDocumentType;
  readonly doc: AdminLegalDocument | undefined;
  readonly selected: boolean;
  readonly onOpen: () => void;
}): ReactElement => {
  const published = doc?.published ?? null;

  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      aria-current={selected ? 'true' : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        width: '100%',
        p: 1.25,
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        border: 0,
        borderRadius: '12px',
        bgcolor: selected ? 'tokens.primarySoft' : 'transparent',
        '&:hover': { bgcolor: selected ? 'tokens.primarySoft' : 'tokens.surfaceMuted' },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'tokens.primary', outlineOffset: 2 },
      }}
    >
      <Box component="span" sx={{ display: 'inline-flex', color: selected ? 'tokens.primary' : 'tokens.textMuted' }}>
        <Icon icon={FileText} size={18} />
      </Box>
      <Box component="span" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <Typography component="span" sx={{ fontSize: 14, fontWeight: 700, color: selected ? 'tokens.primary' : 'tokens.textStrong' }}>
          {LEGAL_DOCUMENT_TITLES[type]}
        </Typography>
        <Typography component="span" sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
          {published?.number === undefined || published.number === null
            ? '—'
            : `v${String(published.number)} · ${formatCompactInstant(published.publishedAt)}`}
        </Typography>
      </Box>
      <Box component="span" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
        {published?.provisional === true ? <Badge label="Provisoire" tone="warning" /> : null}
        {doc?.draft == null ? null : <Badge label="Brouillon" dot />}
      </Box>
    </Box>
  );
};

const Workspace = ({
  api,
  role,
  operator,
  token,
  refresh,
}: {
  readonly api: AdminApi;
  readonly role: AdminRole;
  readonly operator: { readonly id: string; readonly email: string };
  readonly token: number;
  readonly refresh: () => void;
}): ReactElement => {
  const route = useDetailRoute(CONTENT_PATH);
  const selected: AdminLegalDocumentType = isLegalDocumentType(route.selectedId) ? route.selectedId : 'cgu';
  const overview = useAdminQuery('legal-overview', () => api.listLegalDocuments(), { token });
  const documents = overview.data;
  const failure = overview.failure;

  if (failure?.forbidden === true) {
    return (
      <AccessDenied title="Votre rôle ne permet pas de consulter les contenus">
        Votre compte reste connecté : il manque le rôle d’administration requis (au moins « Support »).
      </AccessDenied>
    );
  }

  if (documents === null) {
    return failure === null ? (
      <LoadingState label="Chargement des documents…" />
    ) : (
      <ErrorState message={failure.message} onRetry={refresh} />
    );
  }

  const docFor = (type: AdminLegalDocumentType): AdminLegalDocument | undefined =>
    documents.find((candidate) => candidate.type === type);

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        alignItems: 'start',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(240px, 300px) minmax(0, 1fr)' },
      }}
    >
      <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
        <Card title="Documents légaux">
          <Box component="nav" aria-label="Documents légaux" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {LEGAL_DOCUMENT_TYPES.map((type) => (
              <DocumentRow
                key={type}
                type={type}
                doc={docFor(type)}
                selected={type === selected}
                onOpen={() => {
                  route.open(type);
                }}
              />
            ))}
          </Box>
        </Card>
        {failure === null ? null : <ErrorState message={failure.message} onRetry={refresh} />}
      </Box>

      <DocumentPanel
        // Un document par montage : changer de document remet l'editeur a zero.
        key={selected}
        api={api}
        type={selected}
        doc={docFor(selected)}
        role={role}
        operator={operator}
        token={token}
        onChanged={refresh}
      />
    </Box>
  );
};

export const ContentScreen = (): ReactElement => {
  const http = useHttpClient();
  const api = useMemo(() => createAdminApi(http), [http]);
  const { access, reload } = useAdminAccess();
  const { token, refresh } = useOperationsRefresh();
  const granted = access.kind === 'granted';

  return (
    <Box sx={{ display: 'grid', gap: 2.5, mt: 0.5, mb: 4 }}>
      <PageHeader
        eyebrow="Données"
        title="Contenus"
        subtitle="Textes servis à l’application sans nouvelle publication. Une version publiée ne se modifie plus : on en publie une autre."
        actions={
          granted ? (
            <Button icon={RefreshCw} onClick={refresh}>
              Actualiser
            </Button>
          ) : undefined
        }
      />
      <AccessPanel access={access} onRetry={reload} />
      {access.kind === 'granted' ? (
        <Workspace
          api={api}
          role={access.role}
          operator={{ id: access.identity.userId, email: access.identity.email }}
          token={token}
          refresh={refresh}
        />
      ) : null}
    </Box>
  );
};
